import React, { useEffect, useRef, useState } from 'react';
import FlipCameraAndroidOutlinedIcon from '@mui/icons-material/FlipCameraAndroidOutlined';
import './EventGallery.css';

export default function SelfieCaptureDialog({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [phase, setPhase] = useState('camera');
  const [previewUrl, setPreviewUrl] = useState('');
  const [capturedFile, setCapturedFile] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraKey, setCameraKey] = useState(0);
  const [facingMode, setFacingMode] = useState('user');

  function stopStream() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }

  useEffect(() => {
    if (!open) return;
    setPhase('camera');
    setCapturedFile(null);
    setCameraError('');
    setCameraReady(false);
    setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return ''; });
  }, [open]);

  useEffect(() => {
    if (!open || phase !== 'camera') return;
    let cancelled = false;
    setCameraError('');
    setCameraReady(false);

    async function startCamera() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError('Camera is not available in this browser. Use the gallery option below instead.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode, width: { ideal: 1280 }, height: { ideal: 960 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            if (cancelled || !videoRef.current) return;
            videoRef.current.play()
              .then(() => { if (!cancelled) setCameraReady(true); })
              .catch(() => {});
          };
        }
      } catch (err) {
        if (!cancelled) {
          setCameraError(
            err.name === 'NotAllowedError'
              ? 'Camera permission was denied. Please allow camera access and try again, or use the gallery option below.'
              : 'Could not start the camera. Please check your device settings or use the gallery option below.'
          );
        }
      }
    }

    startCamera();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [open, phase, cameraKey, facingMode]);

  function capture() {
    if (!videoRef.current || !canvasRef.current || !cameraReady) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(blob => {
      if (!blob) return;
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
      setCapturedFile(file);
      setPreviewUrl(URL.createObjectURL(blob));
      setPhase('preview');
      stopStream();
    }, 'image/jpeg', 0.92);
  }

  function switchCamera() {
    stopStream();
    setFacingMode(m => (m === 'user' ? 'environment' : 'user'));
    setCameraKey(k => k + 1);
  }

  function retake() {
    setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return ''; });
    setCapturedFile(null);
    setPhase('camera');
    setCameraKey(k => k + 1);
  }

  function usePhoto() {
    if (capturedFile) onCapture(capturedFile);
    onClose();
  }

  function handleClose() {
    stopStream();
    onClose();
  }

  if (!open) return null;

  return (
    <div className="selfie-cam-overlay" role="dialog" aria-label="Take selfie">
      {phase === 'camera' && (
        <>
          <video
            ref={videoRef}
            className="selfie-cam-video"
            style={facingMode === 'environment' ? { transform: 'none' } : undefined}
            muted
            playsInline
            aria-hidden="true"
          />
          <div className="selfie-cam-oval-guide" aria-hidden="true" />
          {!cameraError && (
            <p className="selfie-cam-hint">
              {cameraReady ? 'Align your face in the oval' : 'Starting camera…'}
            </p>
          )}
          {cameraError && (
            <div className="selfie-cam-error">
              <p>{cameraError}</p>
              <button type="button" onClick={handleClose} className="selfie-cam-error-close">
                Close
              </button>
            </div>
          )}
          {!cameraError && (
            <button
              type="button"
              className="selfie-cam-capture-btn"
              onClick={capture}
              disabled={!cameraReady}
              aria-label="Capture selfie"
            />
          )}
          <button
            type="button"
            className="selfie-cam-switch"
            onClick={switchCamera}
            aria-label="Switch camera"
            title="Switch camera"
          >
            <FlipCameraAndroidOutlinedIcon fontSize="small" />
          </button>
        </>
      )}
      {phase === 'preview' && (
        <>
          <img src={previewUrl} alt="Selfie preview" className="selfie-cam-preview-img" />
          <div className="selfie-cam-preview-label">Use this photo?</div>
          <div className="selfie-cam-preview-controls">
            <button type="button" className="selfie-cam-retake" onClick={retake}>Retake</button>
            <button type="button" className="selfie-cam-use" onClick={usePhoto}>Use Photo</button>
          </div>
        </>
      )}
      <button type="button" className="selfie-cam-back" onClick={handleClose} aria-label="Close camera">
        ✕
      </button>
      <canvas ref={canvasRef} style={{ display: 'none' }} aria-hidden="true" />
    </div>
  );
}
